import { getClassroomApiUrl } from "@/js/utils/getBackendUrl";
import * as clsApi from "@/js/api/classroomApi";
import { ClassroomRule, ClassroomRuleRequest, RuleType, throwRulesLibraryRequestError } from "./rulesLibrary";

/**
 * The applied state of one rule on a classroom. `rule` is the saved rule; the remaining fields
 * describe how it is applied here, so the same library rule can read differently in two classes.
 */
export type AppliedClassroomRule = {
	rule: ClassroomRule;
	/** True when the rule applies to every student in the class; `studentIds` is then empty. */
	wholeClass: boolean;
	/** False when the teacher has switched the rule off in blocking-rules-only mode. */
	enabled: boolean;
	/** True when the rule is enabled and neither conflicted nor overridden. */
	effective: boolean;
	/** ID of the rule this one clashes with, when it is not effective. */
	conflictedBy?: string;
	/** ID of the rule taking precedence over this one, when it is not effective. */
	overriddenBy?: string;
	studentIds: string[];
};

/** A rules library list currently applied to a classroom. */
export type AppliedClassroomRulesList = {
	id: string;
	name: string;
	teacherId: string;
	teacherName: string;
	teacherEmail: string;
	teacherUsername: string;
	classroomRules: AppliedClassroomRule[];
	createdAt: number;
};

export type GetClassroomRulesResponse = {
	appliedLists: AppliedClassroomRulesList[];
	inClassRules: AppliedClassroomRule[];
};

/**
 * Creates a rule that exists on this class only. The rule's `type` is restricted to `URL` and
 * `SIGNATURE`: app controls are a separate resource and are not stored here — see
 * `@/api/classroomAppControls`.
 */
export type CreateInClassRuleRequest = {
	rule: ClassroomRuleRequest;
	wholeClass: boolean;
	enabled: boolean;
	studentIds: string[];
};

/**
 * A partial update of an applied in-class rule. `target` and `type` are not mutable, and omitted
 * fields are left unchanged, so send only what the teacher actually changed.
 */
export type PatchInClassRuleRequest = {
	action?: RuleType;
	enabled?: boolean;
	wholeClass?: boolean;
	studentIds?: string[];
};

function classroomRulesUrl(host: string, applianceId: string, classroomId: string): string {
	return `${host}/v1/appliances/${encodeURIComponent(applianceId)}/classrooms/${encodeURIComponent(classroomId)}/classroom-rules`;
}

export async function getClassroomRules(
	applianceId: string,
	classroomId: string, // UMS stable ID of the classroom.
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetClassroomRulesResponse> {
	const response = await fetch(classroomRulesUrl(host, applianceId, classroomId), {
		method: "GET",
		headers: {
			...authHeaders,
		},
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch classroom rules: ${response.status}`);
	}

	return (await response.json()) as GetClassroomRulesResponse;
}

export async function applyClassroomRulesList(
	applianceId: string,
	classroomId: string,
	listId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetClassroomRulesResponse> {
	const response = await fetch(`${classroomRulesUrl(host, applianceId, classroomId)}/lists/${encodeURIComponent(listId)}`, {
		method: "POST",
		headers: {
			...authHeaders,
		},
	});

	if (!response.ok) {
		await throwRulesLibraryRequestError(response, "Failed to apply rules library list");
	}

	return (await response.json()) as GetClassroomRulesResponse;
}

export async function removeClassroomRulesList(
	applianceId: string,
	classroomId: string,
	listId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetClassroomRulesResponse> {
	const response = await fetch(`${classroomRulesUrl(host, applianceId, classroomId)}/lists/${encodeURIComponent(listId)}`, {
		method: "DELETE",
		headers: {
			...authHeaders,
		},
	});

	if (!response.ok) {
		await throwRulesLibraryRequestError(response, "Failed to remove rules library list");
	}

	return (await response.json()) as GetClassroomRulesResponse;
}

export async function createInClassRule(
	applianceId: string,
	classroomId: string,
	body: CreateInClassRuleRequest,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetClassroomRulesResponse> {
	const response = await fetch(classroomRulesUrl(host, applianceId, classroomId), {
		method: "POST",
		headers: {
			...authHeaders,
		},
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		await throwRulesLibraryRequestError(response, "Failed to create in-class rule");
	}

	return (await response.json()) as GetClassroomRulesResponse;
}

export async function updateInClassRule(
	applianceId: string,
	classroomId: string,
	ruleId: string,
	body: PatchInClassRuleRequest,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetClassroomRulesResponse> {
	const response = await fetch(`${classroomRulesUrl(host, applianceId, classroomId)}/${encodeURIComponent(ruleId)}`, {
		method: "PATCH",
		headers: {
			...authHeaders,
		},
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		await throwRulesLibraryRequestError(response, "Failed to update in-class rule");
	}

	return (await response.json()) as GetClassroomRulesResponse;
}

export async function deleteInClassRule(
	applianceId: string,
	classroomId: string,
	ruleId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetClassroomRulesResponse> {
	const response = await fetch(`${classroomRulesUrl(host, applianceId, classroomId)}/${encodeURIComponent(ruleId)}`, {
		method: "DELETE",
		headers: {
			...authHeaders,
		},
	});

	if (!response.ok) {
		await throwRulesLibraryRequestError(response, "Failed to delete in-class rule");
	}

	return (await response.json()) as GetClassroomRulesResponse;
}

/** Removes every applied list and in-class rule from the classroom. Responds 204 with no body. */
export async function clearClassroomRules(
	applianceId: string,
	classroomId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<void> {
	const response = await fetch(classroomRulesUrl(host, applianceId, classroomId), {
		method: "DELETE",
		headers: {
			...authHeaders,
		},
	});

	if (!response.ok) {
		throw new Error(`Failed to clear classroom rules: ${response.status}`);
	}
}
